import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchAdminPropBots, 
  fetchAdminPropBotById, 
  createPropBot,
  updatePropBot,
  deletePropBot
} from '@/services/admin/admin-prop-bot-service';
import { useState } from 'react';
import { PropBot } from '@/types/prop-bots';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export const useAdminPropBots = () => {
  const [searchParams, setSearchParams] = useState({
    page: 1,
    pageSize: 10,
    searchQuery: '',
    sortBy: 'created_date',
    sortOrder: 'desc' as 'asc' | 'desc',
    status: 'all',
  });

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['adminPropBots', searchParams],
    queryFn: () => fetchAdminPropBots(searchParams),
  });

  const setPage = (page: number) => {
    setSearchParams(prev => ({ ...prev, page }));
  };

  const setPageSize = (pageSize: number) => {
    setSearchParams(prev => ({ ...prev, pageSize, page: 1 }));
  };

  const setSearchQuery = (searchQuery: string) => {
    setSearchParams(prev => ({ ...prev, searchQuery, page: 1 }));
  };

  const setSortBy = (sortBy: string) => {
    setSearchParams(prev => ({ ...prev, sortBy }));
  };

  const setSortOrder = (sortOrder: 'asc' | 'desc') => {
    setSearchParams(prev => ({ ...prev, sortOrder }));
  };

  const setStatus = (status: string) => {
    setSearchParams(prev => ({ ...prev, status, page: 1 }));
  };

  return {
    propBots: data?.bots ?? [],
    totalCount: data?.totalCount ?? 0,
    currentPage: data?.currentPage ?? 1,
    pageSize: data?.pageSize ?? 10,
    totalPages: data?.totalPages ?? 1,
    isLoading,
    isError,
    error,
    refetch,
    searchParams,
    setPage,
    setPageSize,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    setStatus,
  };
};

export const useAdminPropBotDetail = (botId: string | undefined) => {
  return useQuery({
    queryKey: ['adminPropBot', botId],
    queryFn: () => fetchAdminPropBotById(botId!),
    enabled: !!botId,
  });
};

export const useCreatePropBot = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (botData: Partial<PropBot> & { originalLanguage?: string }) => createPropBot(botData),
    retry: false, // Disable retry to prevent duplicate creation
    onSuccess: async (data, variables) => {
      toast.success('Prop bot created successfully');
      queryClient.invalidateQueries({ queryKey: ['adminPropBots'] });
      
      // Auto-translate if original language is specified and not English
      if (variables.originalLanguage && variables.originalLanguage !== 'en' && data?.id) {
        try {
          const content = {
            description: variables.description,
            features: variables.propDetails?.features,
            requirements: variables.propDetails?.requirements,
            challenge_rules: variables.propDetails?.challenge_rules,
          };

          await supabase.functions.invoke('translate-prop-bot-content', {
            body: {
              botId: data.id,
              originalLanguage: variables.originalLanguage,
              content
            }
          });
          
          toast.success('Content translated to multiple languages!');
        } catch (error) {
          console.error('Auto-translation failed:', error);
          toast.error('Bot created but auto-translation failed');
        }
      }
    },
    onError: (error: any) => {
      toast.error(`Error creating prop bot: ${error.message}`);
    }
  });
};

export const useUpdatePropBot = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ botId, botData }: { botId: string; botData: Partial<PropBot> & { originalLanguage?: string } }) => 
      updatePropBot(botId, botData),
    onSuccess: async (_, { botId, botData }) => {
      toast.success('Prop bot updated successfully');
      queryClient.invalidateQueries({ queryKey: ['adminPropBots'] });
      queryClient.invalidateQueries({ queryKey: ['adminPropBot', botId] });
      
      // Auto-translate if original language is specified and not English
      if (botData.originalLanguage && botData.originalLanguage !== 'en') {
        try {
          const content = {
            description: botData.description,
            features: botData.propDetails?.features,
            requirements: botData.propDetails?.requirements,
            challenge_rules: botData.propDetails?.challenge_rules,
          };

          await supabase.functions.invoke('translate-prop-bot-content', {
            body: {
              botId,
              originalLanguage: botData.originalLanguage,
              content
            }
          });
          
          toast.success('Content updated and translated to multiple languages!');
        } catch (error) {
          console.error('Auto-translation failed:', error);
          toast.error('Bot updated but auto-translation failed');
        }
      }
    },
    onError: (error: any) => {
      toast.error(`Error updating prop bot: ${error.message}`);
    }
  });
};

export const useDeletePropBot = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (botId: string) => deletePropBot(botId),
    onSuccess: () => {
      toast.success('Prop bot deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['adminPropBots'] });
    },
    onError: (error: any) => {
      toast.error(`Error deleting prop bot: ${error.message}`);
    }
  });
};
